import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { type ColumnDef } from "@tanstack/react-table";
import { Users, RefreshCw, Search, FolderTree } from "lucide-react";
import { useGroupsList } from "@/hooks/use-groups";
import type { KeycloakGroup } from "@/api/users";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { Badge, Button, Input } from "@fox1ck-org/ui";

interface FlatGroup extends KeycloakGroup {
  depth: number;
  parentPath: string;
}

function flatten(groups: KeycloakGroup[] | undefined, depth = 0, parentPath = ""): FlatGroup[] {
  if (!groups) return [];
  const out: FlatGroup[] = [];
  for (const g of groups) {
    out.push({ ...g, depth, parentPath });
    if (g.subGroups && g.subGroups.length > 0) {
      out.push(...flatten(g.subGroups, depth + 1, g.path ?? `${parentPath}/${g.name}`));
    }
  }
  return out;
}

const columns: ColumnDef<FlatGroup>[] = [
  {
    accessorKey: "name",
    header: "Название",
    cell: ({ row }) => {
      const g = row.original;
      const indent = "·".repeat(g.depth * 2);
      return (
        <Link
          to={`/groups/${g.id}`}
          className="flex items-center gap-2 font-medium text-foreground hover:text-primary"
        >
          {g.depth > 0 && <span className="text-muted-foreground/40">{indent}</span>}
          {g.depth === 0 ? (
            <FolderTree className="h-4 w-4 text-muted-foreground" />
          ) : (
            <Users className="h-4 w-4 text-muted-foreground" />
          )}
          {g.name}
        </Link>
      );
    },
  },
  {
    accessorKey: "path",
    header: "Путь",
    cell: ({ row }) => (
      <code className="text-xs text-muted-foreground">{row.original.path ?? ""}</code>
    ),
  },
  {
    accessorKey: "subGroups",
    header: "Дочерние",
    cell: ({ row }) => {
      const n = row.original.subGroups?.length ?? 0;
      if (n === 0) return <span className="text-muted-foreground">—</span>;
      return (
        <Badge variant="neutral">
          {n} child{n === 1 ? "" : "ren"}
        </Badge>
      );
    },
  },
];

export default function Groups() {
  const [search, setSearch] = useState("");
  const { data, isLoading, refetch } = useGroupsList();
  const navigate = useNavigate();

  const groups = useMemo(() => flatten(data?.groups), [data]);

  const filtered = useMemo(() => {
    if (!search) return groups;
    const q = search.toLowerCase();
    return groups.filter(
      (g) =>
        g.name.toLowerCase().includes(q) ||
        (g.path && g.path.toLowerCase().includes(q)),
    );
  }, [groups, search]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Группы"
        description="Группы Keycloak и их иерархия. Откройте группу, чтобы управлять участниками."
      >
        <Button variant="ghost" size="icon" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </PageHeader>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Поиск по названию или пути…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <span className="text-sm text-muted-foreground">
          {filtered.length} group{filtered.length === 1 ? "" : "s"}
        </span>
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        isLoading={isLoading}
        onRowClick={(row) => navigate(`/groups/${row.id}`)}
        emptyState={
          <EmptyState
            icon={<Users className="h-10 w-10" />}
            title={search ? "Ничего не нашлось" : "Групп нет"}
            description={
              search
                ? "Попробуйте другой запрос."
                : "Здесь появятся группы Keycloak. Они создаются в Terraform или в админке Keycloak."
            }
          />
        }
      />
    </div>
  );
}
